import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMiniQuiz } from '../contexts/MiniQuizContext';
import { useAuth } from '../contexts/AuthContext';
import { taskApi } from '../services/taskApi';
import { MiniQuizSubmitResponse } from '../types';
import RewardModal from '../components/RewardModal';
import './MiniQuizPage.css';
import { BaseLayout } from '@/components';

const MiniQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFromTask = searchParams.get('fromTask') === 'true';
  
  const { isAuthenticated } = useAuth();
  const {
    session,
    questions,
    currentQuestionIndex,
    remainingAttempts,
    isLoading,
    isAnswering,
    error,
    startQuiz,
    submitAnswer,
    resetQuiz,
    clearError,
    getRemainingAttempts
  } = useMiniQuiz();

  console.log("Current Question Index: " + currentQuestionIndex);

  const [showResult, setShowResult] = useState(false);
  const [lastAnswerResult, setLastAnswerResult] = useState<MiniQuizSubmitResponse | null>(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [quizSummary, setQuizSummary] = useState<MiniQuizSubmitResponse['summary'] | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  // Swipe states
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  // Task-specific states
  const [taskQuestion, setTaskQuestion] = useState<any>(null);
  const [taskUserTask, setTaskUserTask] = useState<any>(null);
  const [taskIsLoading, setTaskIsLoading] = useState(false);
  const [taskIsAnswering, setTaskIsAnswering] = useState(false);

  // Fetch remaining attempts when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && !isFromTask) {
      getRemainingAttempts();
    }
  }, [isAuthenticated, getRemainingAttempts, isFromTask]);

  useEffect(() => {
    if (error) {
      alert(error);
      clearError();
    }
  }, [error, clearError]);

  const handleStartQuiz = async () => {
    if (isFromTask) {
      // Use task-specific API
      setTaskIsLoading(true);
      try {
        const response = await taskApi.startMiniQuizTask();
        if (response.success && response.data) {
          setTaskUserTask(response.data.userTask);
          setTaskQuestion(response.data.question);
        }
      } catch (error) {
        console.error('Error starting mini quiz task:', error);
        alert('Lỗi khi bắt đầu nhiệm vụ. Vui lòng thử lại!');
      } finally {
        setTaskIsLoading(false);
      }
    } else {
      // Use normal mini quiz flow
      const success = await startQuiz();
      if (success) {
        setShowResult(false);
        setLastAnswerResult(null);
      }
    }
  };

  const handleSubmitAnswer = async (answer: boolean) => {
    if (isFromTask) {
      // Use task-specific API
      if (!taskUserTask) return;
      
      setTaskIsAnswering(true);
      try {
        const response = await taskApi.answerMiniQuizTask(taskUserTask.id, answer);
        if (response.success && response.data) {
          const result = response.data;
          
          // Show result for 3 seconds, then show explanation if available
          setLastAnswerResult({
            isCorrect: result.isCorrect,
            score: result.isCorrect ? 1 : 0,
            currentQuestionIndex: 0,
            isCompleted: true,
            energyRewarded: false,
            explanation: result.explanation,
            summary: undefined
          });
          setShowResult(true);
          
          setTimeout(() => {
            if (result.explanation) {
              setShowResult(false);
              setShowExplanation(true);
              
              // Show explanation for 4 seconds then navigate back
              setTimeout(() => {
                navigate(-2);
              }, 4000);
            } else {
              navigate(-2);
            }
          }, 3000);
        }
      } catch (error) {
        console.error('Error answering task question:', error);
        alert('Lỗi khi trả lời câu hỏi. Vui lòng thử lại!');
      } finally {
        setTaskIsAnswering(false);
      }
    } else {
      // Use normal mini quiz flow
      const questionId = questions[currentQuestionIndex]?.id;
      console.log("Question ID: " + currentQuestionIndex);
      if (!questionId) return;
      
      const result = await submitAnswer(questionId, answer);
      if (result) {
        setLastAnswerResult(result);
        setShowResult(true);

        if (result.isCompleted) {
          setTimeout(() => {
            setShowResult(false);
            if (result.summary) {
              setQuizSummary(result.summary);
              setShowRewardModal(true);
            }
          }, result.explanation ? 3000 : 1500);
        } else {
          setTimeout(() => {
            if (result.explanation) {
              setShowResult(false);
              setShowExplanation(true);
              
              // Show explanation for 3 seconds then continue
              setTimeout(() => {
                setShowExplanation(false);
              }, 3000);
            } else {
              setShowResult(false);
            }
          }, 2000);
        }
      }
    }
  };

  const getCurrentQuestion = () => {
    if (isFromTask) {
      return taskQuestion;
    }
    return questions[currentQuestionIndex];
  };

  const getPageTitle = () => {
    return isFromTask ? 'Nhiệm vụ Mini Quiz' : 'Mini Quiz';
  };

  const handleCloseRewardModal = () => {
    setShowRewardModal(false);
    setQuizSummary(null);
    resetQuiz();
  };

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const isTaskAnswering = isFromTask ? taskIsAnswering : isAnswering;
    if (isTaskAnswering) return;
    
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    setCurrentX(e.touches[0].clientX);
    const offset = e.touches[0].clientX - startX;
    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const swipeThreshold = 80; // Minimum distance for swipe
    const offset = currentX - startX;
    
    if (Math.abs(offset) > swipeThreshold) {
      if (offset > 0) {
        // Swipe right = Yes/True
        handleSubmitAnswer(true);
      } else {
        // Swipe left = No/False
        handleSubmitAnswer(false);
      }
    }
    
    // Reset states
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const isTaskAnswering = isFromTask ? taskIsAnswering : isAnswering;
    if (isTaskAnswering) return;
    
    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    setCurrentX(e.clientX);
    const offset = e.clientX - startX;
    setDragOffset(offset);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const swipeThreshold = 80;
    const offset = currentX - startX;
    
    if (Math.abs(offset) > swipeThreshold) {
      if (offset > 0) {
        handleSubmitAnswer(true);
      } else {
        handleSubmitAnswer(false);
      }
    }
    
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleBackClick = () => {
    if (isFromTask) {
      navigate(-1);
    } else {
      navigate(-1);
    }
  };

  // Loading screen for task or mini game
  if ((isFromTask && taskIsLoading) || (!isFromTask && isLoading)) {
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
        </div>
        
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải...</p>
        </div>
      </div>
    );
  }

  // Mini game welcome screen 
  if (!session && !isFromTask) {
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
        </div>
        
        <div className="welcome-container">
          <div className="welcome-content">
            <div className="quiz-info">
              <h2>Chào mừng đến với Mini Quiz!</h2>
              <div className="quiz-rules">
                <p>• 10 câu hỏi về KienlongBank</p>
                <p>• Trả lời Đúng/Sai</p>
                <p>• Cần đúng 7/10 câu để nhận 2 năng lượng</p>
                <p>• Giới hạn: 3 lượt nhận thưởng/ngày</p>
              </div>
              <div className="attempts-info">
                <p>Lượt chơi còn lại: <strong>{remainingAttempts}/3</strong></p>
              </div>
            </div>
            
            <button 
              className="start-quiz-button"
              onClick={handleStartQuiz}
              disabled={remainingAttempts <= 0}
            >
              {remainingAttempts > 0 ? 'Bắt đầu Quiz' : 'Hết lượt chơi'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Task welcome screen
  if (isFromTask && !taskQuestion) {
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
        </div>
        
        <div className="welcome-container">
          <div className="welcome-content">
            <div className="quiz-info">
              <h2>Nhiệm vụ Mini Quiz!</h2>
              <div className="quiz-rules">
                <p>• 1 câu hỏi về KienlongBank</p>
                <p>• Trả lời Đúng/Sai</p>
                <p>• Trả lời đúng để hoàn thành nhiệm vụ</p>
                <p>• Quay lại màn hình nhiệm vụ để nhận thưởng</p>
              </div>
            </div>
            
            <button 
              className="start-quiz-button"
              onClick={handleStartQuiz}
              disabled={taskIsLoading}
            >
              {taskIsLoading ? 'Đang tải...' : 'Bắt đầu nhiệm vụ'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = getCurrentQuestion();
  
  // Result screen
  if (showResult && lastAnswerResult) {
    const result = lastAnswerResult;
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          {/* <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button> */}
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
        </div>

        <div className="result-container">
          <div className={`result-feedback ${result.isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="result-icon">
              {result.isCorrect ? '✓' : '✗'}
            </div>
            <h2>{result.isCorrect ? 'Chính xác!' : 'Sai rồi!'}</h2>
            {!isFromTask && (
              <p>Điểm: {result.score}/{questions.length}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Explanation screen
  if (showExplanation && lastAnswerResult?.explanation) {
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          {/* <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button> */}
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
        </div>

        <div className="explanation-container">
          <div className="explanation-content">
            <h2>💡 Giải thích</h2>
            <p className="explanation-text">{lastAnswerResult.explanation}</p>
            {!isFromTask && (
              <p className="next-hint">Câu hỏi tiếp theo sẽ xuất hiện sau vài giây...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Question screen
  if (currentQuestion) {
    const isTaskAnswering = isFromTask ? taskIsAnswering : isAnswering;
    const currentIndex = isFromTask ? 0 : currentQuestionIndex;
    const totalQuestions = isFromTask ? 1 : questions.length;
    
    return (
      <div className="mini-quiz-page">
        <div className="mini-quiz-header">
          <button className="mini-game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
          
        </div>
        {!isFromTask && session && (
            <span style={{color: 'white'}}>Câu {currentIndex + 1}: {session.score || 0}/{totalQuestions}</span>
        )}
        <div className='question-area'>
          <div className="question-container">
            <div 
              className="question-card"
              ref={cardRef}
              style={{
                transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.1}deg)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease'
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Swipe indicators */}
              <div 
                className="swipe-indicator left"
                style={{
                  opacity: dragOffset < -30 ? Math.min(1, Math.abs(dragOffset) / 100) : 0
                }}
              >
                ✗ SAI
              </div>
              <div 
                className="swipe-indicator right"
                style={{
                  opacity: dragOffset > 30 ? Math.min(1, dragOffset / 100) : 0
                }}
              >
                ✓ ĐÚNG
              </div>

              <div 
                className="question-image"
                style={{
                  backgroundImage: `url(${currentQuestion.category ? `/images/miniquiz/${currentQuestion.category}.jpg` : '/images/miniquiz/30nam.jpg'})`,
                }}
              />
              
              <div className="question-content">
                <p className="question-text">{currentQuestion.question}</p>
              </div>
            </div>

            <div className='answer-section'>
              <div className="answer-buttons">
                <button 
                  className={`answer-button no-button ${isTaskAnswering ? 'disabled' : ''}`}
                  onClick={() => handleSubmitAnswer(false)}
                  disabled={isTaskAnswering}
                >
                  {isTaskAnswering ? 'Đang xử lý...' : 'Sai'}
                </button>
                <button 
                  className={`answer-button yes-button ${isTaskAnswering ? 'disabled' : ''}`}
                  onClick={() => handleSubmitAnswer(true)}
                  disabled={isTaskAnswering}
                >
                  {isTaskAnswering ? 'Đang xử lý...' : 'Đúng'}
                </button>
              </div>
              <div className='swipe-hint-container'>
                <p className="swipe-hint">Quẹt trái/phải để chọn đáp án Sai/Đúng</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Quiz summary screen (only for mini game)
  if (!isFromTask && quizSummary) {
    return (
      <>
        <div className="mini-quiz-page">
          <div className="mini-quiz-header">
            <button className="mini-game-back-btn" onClick={handleBackClick}>
              <img src="/images/icons/back.svg" alt="Back" />
            </button>
            <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
          </div>
        </div>
        
        {showRewardModal && (
          <RewardModal
            isOpen={showRewardModal}
            onClose={handleCloseRewardModal}
            title={quizSummary.passStatus === 'passed' ? '🎉 Chúc mừng!' : '😔 Chưa đạt yêu cầu'}
            body1={
              quizSummary.passStatus === 'passed'
                ? `Bạn đã trả lời đúng ${quizSummary.correctAnswers}/${quizSummary.totalQuestions} câu và nhận được ${quizSummary.energyEarned} năng lượng!`
                : `Bạn đã trả lời đúng ${quizSummary.correctAnswers}/${quizSummary.totalQuestions} câu. Cần ít nhất 7 câu đúng để nhận thưởng.`
            }
          />
        )}
      </>
    );
  }

  // Default fallback - should not reach here
  return (
    <div className="mini-quiz-page">
      <div className="mini-quiz-header">
        <button className="mini-game-back-btn" onClick={handleBackClick}>
          <img src="/images/icons/back.svg" alt="Back" />
        </button>
        <h1 className="mini-quiz-title" style={{marginTop: 12}}>{getPageTitle()}</h1>
      </div>
      
      <div className="loading-container">
        <p>Đang tải...</p>
      </div>
    </div>
  );
};


const MiniQuizPageContainer: React.FC = () => {
  return (
    <div className="viewport-wrapper">
        <MiniQuizPage />
    </div>
  )
};

export default MiniQuizPageContainer;
